import { useSelector } from 'react-redux';
import { useMemo } from 'react';

/**
 * Hook para verificar permisos por módulo y acción específica (controlar, actualizar)
 * @param {string} modulo - El nombre del módulo ('sensor', 'tarjeta', etc.)
 * @returns {Object} Permisos para el módulo: puedeControlar, puedeActualizar, esAdmin, esSuperAdmin
 */

export const useModuloPermissions = (modulo) => {
  const { user } = useSelector(state => state.userReducer);
  const { privilegios, privilegiosUsuario } = useSelector(state => state.privilegiosReducer);

  // Usar privilegiosUsuario si existen, si no privilegios globales (admin panel)
  const privilegiosFuente = privilegiosUsuario && privilegiosUsuario.length > 0 ? privilegiosUsuario : privilegios;

  const permissions = useMemo(() => {
    if (!user) {
      return {
        puedeControlar: false,
        puedeActualizar: false,
        esAdmin: false,
        esSuperAdmin: false
      };
    }

    const esAdmin = user.rol === 'admin';
    const esSuperAdmin = user.rol === 'superadmin';

    if (esSuperAdmin) {
      return {
        puedeControlar: true,
        puedeActualizar: true,
        esAdmin: false,
        esSuperAdmin: true
      };
    }
    if (esAdmin) {
      return {
        puedeControlar: true,
        puedeActualizar: true,
        esAdmin: true,
        esSuperAdmin: false
      };
    }

    // Para otros usuarios, buscar privilegios por módulo
    const privilegiosModulo = privilegiosFuente.filter(p =>
      p.modulo === modulo && p.activo && (p.esVigente === undefined || p.esVigente === true)
    );

    // Accion "consultar" para ver el panel
    const puedeConsultar = privilegiosModulo.some(p =>
      p.acciones.includes('consultar') || p.acciones.includes('all')
    );
    // Accion "controlar" para accionar
    const puedeControlar = privilegiosModulo.some(p =>
      p.acciones.includes('controlar') || p.acciones.includes('all')
    );
    // Accion "actualizar" para actualizar
    const puedeActualizar = privilegiosModulo.some(p =>
      p.acciones.includes('actualizar') || p.acciones.includes('all')
    );

    return {
      puedeConsultar,
      puedeControlar,
      puedeActualizar,
      esAdmin: false,
      esSuperAdmin: false
    };
  }, [user, privilegiosFuente, modulo]);

  return permissions;
};

export default useModuloPermissions;
